// =============================================
// Shared helpers for the active-workout flow.
// Extracted verbatim from ActiveWorkoutInterface.js (Phase A decomposition) —
// pure functions / constants only, no behavior change.
// =============================================

// Single source of truth for the save-progress body used by the immediate
// save, the debounced autosave, and the offline draft buffer.
export const buildExercisesToSave = (data, exerciseNotes = {}) =>
  (data || []).map((ex, idx) => ({
    exercise_id: ex.exercise_id || '',
    exercise_name: ex.exercise_name || 'Unknown',
    sets: ex.target_sets || ex.sets || 3,
    target_sets: ex.target_sets || ex.sets || 3,
    reps: ex.target_reps || ex.reps || 12,
    target_reps: ex.target_reps || ex.reps || 12,
    weight: ex.weight || 0,
    rest_time: ex.rest_time || 120,
    actual_sets: ex.actual_sets || [],
    note: exerciseNotes[idx] || ex.note || '',
    notes: exerciseNotes[idx] || ex.notes || '',
    is_superset: ex.is_superset || false,
    superset_group: ex.superset_group || null,
    image_url: ex.image_url || ex.gifUrl || null,
    animation_webp: ex.animation_webp || null
  }));

// Default PIN for coach mode
export const DEFAULT_COACH_PIN = '1234';

// AUTH HELPER — Get authorization headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// RPE MAPPING — Spanish client-facing labels
export const getRpeLabel = (rpe) => {
  if (!rpe || rpe < 1) return null;
  if (rpe <= 4) return { text: 'Fácil', helper: 'Hay que subir peso o dificultad', color: 'green' };
  if (rpe <= 6) return { text: 'Buen esfuerzo', helper: 'Podías hacer más', color: 'yellow' };
  if (rpe <= 8) return { text: 'Objetivo conseguido', helper: 'Cerca del fallo, casi al límite', color: 'orange' };
  return { text: 'Esfuerzo máximo', helper: 'Fallo muscular o menos repeticiones de las marcadas', color: 'red' };
};

// REST TIME COLOR MAPPING
export const getRestTimeColor = (restSeconds) => {
  const restTime = parseInt(restSeconds) || 120;
  if (restTime <= 60) return 'green';      // 0.5-1 min: Low fatigue
  if (restTime <= 120) return 'orange';    // 1.5-2 min: Moderate fatigue
  return 'red';                            // 3 min: High fatigue
};

export const getRestColorClasses = (color) => {
  switch (color) {
    case 'green':
      return {
        bg: 'bg-green-500/20',
        border: 'border-green-400',
        text: 'text-green-200',
        timer: 'text-green-100',
        progress: 'bg-green-400'
      };
    case 'orange':
      return {
        bg: 'bg-orange-500/20',
        border: 'border-orange-400',
        text: 'text-orange-200',
        timer: 'text-orange-100',
        progress: 'bg-orange-400'
      };
    case 'red':
      return {
        bg: 'bg-red-500/20',
        border: 'border-red-400',
        text: 'text-red-200',
        timer: 'text-red-100',
        progress: 'bg-red-400'
      };
    default:
      return {
        bg: 'bg-yellow-500/20',
        border: 'border-yellow-400',
        text: 'text-yellow-200',
        timer: 'text-yellow-100',
        progress: 'bg-yellow-400'
      };
  }
};

// Experience-based session rating labels (NOT technical RPE)
export const getSessionRatingLabel = (rating) => {
  if (!rating || rating < 1) return null;
  if (rating <= 2) return { text: 'Muy mala', helper: 'Algo falló hoy, ¡mañana será mejor!', color: 'red' };
  if (rating <= 4) return { text: 'Regular', helper: 'No fue tu mejor día, pero seguimos', color: 'orange' };
  if (rating <= 6) return { text: 'Bien', helper: 'Sesión correcta, buen trabajo', color: 'yellow' };
  if (rating <= 8) return { text: 'Muy bien', helper: '¡Gran sesión! Sigue así', color: 'green' };
  return { text: '¡Increíble!', helper: '¡Sesión épica! Te has superado', color: 'green' };
};
